import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Check, Plus, Minus } from "lucide-react";
import { PIZZAS_COMBINADAS } from "@/data/menuData";
import { useCart } from "@/context/CartContext";

const PROMOS = [
  {
    id: "2-medianas",
    title: "2 Pizzas Medianas",
    price: 299,
    sizeLabel: "Mediana",
    badge: "Promo",
    accent: "#C41E3A",
  },
  {
    id: "2-grandes",
    title: "2 Pizzas Grandes",
    price: 399,
    sizeLabel: "Grande",
    badge: "Promo",
    accent: "#2B3618",
  },
];

const MAX_FLAVORS_PER_PIZZA = 2;

const PromoCustomizer = ({ promo, onClose }) => {
  const { addItem } = useCart();
  const [pizza1, setPizza1] = useState([]);
  const [pizza2, setPizza2] = useState([]);
  const [notes, setNotes] = useState("");
  const [qty, setQty] = useState(1);

  const togglePizza = (setter, list, name) => {
    setter((prev) => {
      if (prev.includes(name)) return prev.filter((n) => n !== name);
      if (prev.length >= MAX_FLAVORS_PER_PIZZA) return [...prev.slice(1), name];
      return [...prev, name];
    });
  };

  const canSubmit = pizza1.length > 0 && pizza2.length > 0;

  const handleAdd = () => {
    if (!canSubmit) return;
    const itemName = `${promo.title} (${promo.sizeLabel}) — P1: ${pizza1.join(" / ")} | P2: ${pizza2.join(" / ")}`;
    addItem({
      type: "promo",
      name: itemName,
      category: "Promociones",
      basePrice: promo.price,
      unitPrice: promo.price,
      notes: notes.trim(),
      quantity: qty,
    });
    onClose();
  };

  const renderFlavorPicker = (selected, setter, idx) => (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <h5 className="font-['DM_Sans'] text-xs uppercase tracking-[0.15em] font-bold text-[#556B2F]">
          Pizza {idx} ({selected.length}/{MAX_FLAVORS_PER_PIZZA})
        </h5>
        {selected.length > 0 && (
          <button
            onClick={() => setter([])}
            className="text-[11px] font-['DM_Sans'] text-[#C41E3A] hover:underline"
          >
            Limpiar
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-[200px] overflow-y-auto pr-1">
        {PIZZAS_COMBINADAS.map((f) => {
          const isSel = selected.includes(f.name);
          return (
            <button
              key={f.name}
              data-testid={`promo-pizza-${idx}-flavor-${f.name.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => togglePizza(setter, selected, f.name)}
              className={`text-left rounded-lg border-2 p-2 transition-all relative ${
                isSel
                  ? "border-[#C41E3A] bg-[#C41E3A]/10"
                  : "border-[#1A1A1A]/12 bg-white hover:border-[#C41E3A]/40"
              }`}
            >
              {isSel && (
                <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-[#C41E3A] text-white flex items-center justify-center">
                  <Check className="w-2.5 h-2.5" />
                </span>
              )}
              <span className="block font-['DM_Sans'] text-[11px] font-bold text-[#1A1A1A] pr-4">
                {f.name}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div
      data-testid="promo-customizer"
      className="fixed inset-0 z-[400] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 50, opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#FAF7F0] w-full sm:max-w-2xl sm:rounded-2xl rounded-t-3xl max-h-[92vh] flex flex-col shadow-2xl"
      >
        <div className="flex items-start justify-between p-5 border-b border-[#1A1A1A]/10 bg-[#2B3618] text-white sm:rounded-t-2xl rounded-t-3xl">
          <div className="flex-1 min-w-0 pr-3">
            <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-bold text-[#F5C242] mb-1">
              Promoción
            </span>
            <h3 className="font-['Playfair_Display'] text-xl md:text-2xl font-bold">{promo.title}</h3>
            <p className="font-['DM_Sans'] text-xs sm:text-sm text-white/80 mt-1">
              Elige hasta 2 sabores en cada pizza (solo combinadas) — <b>${promo.price}</b>
            </p>
          </div>
          <button
            data-testid="promo-cust-close"
            onClick={onClose}
            className="w-9 h-9 bg-white/15 hover:bg-white/25 rounded-full flex items-center justify-center"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-5 flex-1">
          {renderFlavorPicker(pizza1, setPizza1, 1)}
          {renderFlavorPicker(pizza2, setPizza2, 2)}

          <div>
            <h5 className="font-['DM_Sans'] text-xs uppercase tracking-[0.15em] font-bold text-[#556B2F] mb-2">
              Notas (opcional)
            </h5>
            <textarea
              data-testid="promo-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ej. Sin cebolla, extra queso..."
              rows={2}
              className="w-full bg-white border border-[#1A1A1A]/15 rounded-xl p-3 text-sm font-['DM_Sans'] focus:outline-none focus:border-[#556B2F]"
            />
          </div>
        </div>

        <div className="border-t border-[#1A1A1A]/10 p-4 bg-white sm:rounded-b-2xl flex items-center gap-3">
          <div className="flex items-center bg-[#F3F4F6] rounded-full">
            <button
              data-testid="promo-qty-decrease"
              onClick={() => setQty((q) => Math.max(1, q - 1))}
              className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[#E5E7EB]"
            >
              <Minus className="w-4 h-4" />
            </button>
            <span className="w-7 text-center font-bold font-['DM_Sans']">{qty}</span>
            <button
              data-testid="promo-qty-increase"
              onClick={() => setQty((q) => q + 1)}
              className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-[#E5E7EB]"
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <button
            data-testid="promo-add-to-cart"
            disabled={!canSubmit}
            onClick={handleAdd}
            className="flex-1 bg-[#C41E3A] hover:bg-[#9A172D] disabled:bg-[#9A172D]/40 disabled:cursor-not-allowed text-white font-['DM_Sans'] font-bold py-3 rounded-full transition-colors"
          >
            Agregar ${promo.price * qty}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export const PromoBanner = () => {
  const [listOpen, setListOpen] = useState(false);
  const [activePromo, setActivePromo] = useState(null);

  return (
    <>
      {/* Floating cloud bubble */}
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1, y: [0, -6, 0] }}
        transition={{
          scale: { duration: 0.5, delay: 0.4 },
          opacity: { duration: 0.5, delay: 0.4 },
          y: { duration: 2.4, repeat: Infinity, ease: "easeInOut" },
        }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setListOpen(true)}
        data-testid="floating-promo-btn"
        className="fixed left-4 sm:left-6 bottom-5 sm:bottom-8 z-[180] flex flex-col items-center group"
      >
        <span className="relative bg-gradient-to-br from-[#FFD93D] via-[#FFB800] to-[#F59E0B] text-[#1A1A1A] font-['Playfair_Display'] font-bold rounded-full shadow-xl shadow-black/30 w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center border-4 border-white">
          <span className="absolute inset-0 rounded-full bg-white/30 animate-ping opacity-50" />
          <div className="text-center leading-none">
            <Sparkles className="w-4 h-4 mx-auto mb-0.5 text-[#C41E3A]" />
            <span className="block text-[10px] sm:text-[11px] font-extrabold tracking-tight">
              PROMOS
            </span>
          </div>
        </span>
      </motion.button>

      {/* Promo list modal */}
      <AnimatePresence>
        {listOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={() => setListOpen(false)}
            className="fixed inset-0 z-[300] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 30 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="promo-list-modal"
              className="bg-[#FAF7F0] w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
            >
              <div className="bg-gradient-to-r from-[#C41E3A] to-[#9A172D] text-white p-5 flex items-center justify-between">
                <div>
                  <span className="text-[10px] uppercase tracking-[0.25em] font-bold text-yellow-300">
                    Sólo a domicilio
                  </span>
                  <h3 className="font-['Playfair_Display'] text-2xl font-bold mt-1">
                    Promociones Adrianoz
                  </h3>
                </div>
                <button
                  data-testid="promo-list-close"
                  onClick={() => setListOpen(false)}
                  className="w-9 h-9 bg-white/15 hover:bg-white/25 rounded-full flex items-center justify-center"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-4">
                {PROMOS.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`promo-card-${p.id}`}
                    onClick={() => {
                      setActivePromo(p);
                      setListOpen(false);
                    }}
                    className="w-full text-left bg-white border-2 border-[#1A1A1A]/10 hover:border-[#C41E3A] rounded-2xl p-5 transition-all group hover:shadow-lg"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <span
                          className="inline-block text-[10px] uppercase tracking-[0.2em] font-bold mb-1.5 px-2 py-0.5 rounded-full text-white"
                          style={{ backgroundColor: p.accent }}
                        >
                          {p.badge}
                        </span>
                        <h4 className="font-['Playfair_Display'] text-lg font-bold text-[#1A1A1A] leading-tight">
                          {p.title}
                        </h4>
                        <p className="font-['DM_Sans'] text-xs text-[#4A4A4A] mt-1">
                          Elige hasta 2 sabores en cada pizza (combinadas)
                        </p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className="block font-['Playfair_Display'] text-3xl font-bold text-[#C41E3A]">
                          ${p.price}
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
                <p className="text-[11px] text-center text-[#4A4A4A] font-['DM_Sans'] mt-2">
                  🚚 Aplica solo en pedidos a domicilio. Sabores combinados disponibles.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Promo customizer (pick flavors) */}
      <AnimatePresence>
        {activePromo && (
          <PromoCustomizer promo={activePromo} onClose={() => setActivePromo(null)} />
        )}
      </AnimatePresence>
    </>
  );
};
